import { MERCHANT_TERMS } from '@field-support/shared';
import type { importAr } from './import.ar';

/** Bulk import copy (English). Must cover exactly the same keys as `importAr`. */
export const importEn: typeof importAr = {
  import: {
    title: `Bulk import ${MERCHANT_TERMS.en.manyLower} & branches`,
    description: `Creates or updates ${MERCHANT_TERMS.en.manyLower} and branches from a CSV or Excel file.`,
    help: 'Download the template, fill in one row per branch, then choose the file here. The template has an Instructions sheet explaining every column. Headers in English or Arabic are both accepted.',
    requiredColumns: 'Required columns: {{columns}}.',
    optionalColumns: 'Optional: {{columns}}.',
    // Each format is isolated (<bdi>) so the Latin tokens keep their order inside Arabic.
    fileTypes: '<xlsx>Excel (.xlsx)</xlsx> or <csv>CSV (.csv)</csv>',
    downloadTemplate: 'Download template',
    unsupportedFile: 'This file type is not supported. Use the template (.xlsx) or save the sheet as .csv.',
    unreadableFile: 'The file could not be read: {{detail}}',
    template: {
      dataSheet: 'Import',
      instructionsSheet: 'Instructions',
      column: 'Column',
      required: 'Required',
      format: 'Accepted format',
      example: 'Example',
      failed: 'The template could not be generated.',
    },
    statusIdle: 'No file loaded',
    statusParsed: 'rows read from',
    statusParsed_one: 'row read from',
    statusParsed_two: 'rows read from',
    statusParsed_few: 'rows read from',
    chooseFile: 'Choose file',
    parsed: '{{count}} rows read from {{file}}',
    parsed_one: '{{count}} row read from {{file}}',
    parsed_two: '{{count}} rows read from {{file}}',
    parsed_few: '{{count}} rows read from {{file}}',
    missingColumns: 'Missing required columns: {{columns}}. Download the template to get the exact headers.',
    clientErrors: '{{count}} rows have errors and will be reported by the server',
    clientErrors_one: '{{count}} row has errors and will be reported by the server',
    clientErrors_two: '{{count}} rows have errors and will be reported by the server',
    clientErrors_few: '{{count}} rows have errors and will be reported by the server',
    updateExisting: 'Update existing branches',
    dryRun: 'Preview (dry run)',
    import: 'Import',
    running: 'Sending rows to the server',
    previewTitle: 'Import preview (dry run)',
    finished: 'Import finished',
    summary: `${MERCHANT_TERMS.en.many} created: {{createdBrands}} · Branches created: {{createdBranches}} · Updated: {{updatedBranches}} · Skipped: {{skipped}} · Errors: {{errors}}`,
    rowsWithoutError: 'rows without errors',
    row: 'Row',
    outcome: 'Result',
    message: 'Details',
    brandCreated: `New ${MERCHANT_TERMS.en.oneLower}`,
    onlyProblems: 'Errors and skipped rows only',
    dryRunDone: 'Preview only – nothing was saved.',
    previewRows: 'Rows in the file',
    previewHint: 'Checked in the browser before sending. The dry run asks the server and saves nothing.',
    previewShown: 'Showing {{shown}} of {{total}} rows. Every row with a problem is listed.',
    check: 'Check',
    checkOk: 'OK',
    checkFailed: 'Problem',
    issueLine: '{{column}}: {{problem}}',
    tooManyRows: 'The file has {{count}} rows; one import takes at most {{max}}. Split the file.',
    /** Why a report row is an error or skipped (BULK_IMPORT_ROW_REASONS). */
    reason: {
      INVALID_ROW: 'The row is not valid: {{issues}}', // new — needs native review
      GEOFENCE_NOT_PERMITTED: 'A geofence radius needs the “Set geofence radius” permission.', // new — needs native review
      DUPLICATE_ROW: `Same ${MERCHANT_TERMS.en.oneLower} and branch as row {{row}}.`, // new — needs native review
      DUPLICATE_BRANCH_CODE_IN_FILE: 'Branch code {{code}} is also used in row {{row}}.', // new — needs native review
      MERCHANT_DELETED: `${MERCHANT_TERMS.en.one} {{merchant}} is deleted.`, // new — needs native review
      MERCHANT_CODE_CONFLICT_IN_FILE: `${MERCHANT_TERMS.en.one} code {{code}} differs from {{otherCode}}, given for the same ${MERCHANT_TERMS.en.oneLower} in row {{row}}.`, // new — needs native review
      MERCHANT_CODE_SHARED_IN_FILE: `${MERCHANT_TERMS.en.one} code {{code}} is used for more than one ${MERCHANT_TERMS.en.oneLower} in the file.`, // new — needs native review
      MERCHANT_CODE_TAKEN: `${MERCHANT_TERMS.en.one} code {{code}} belongs to another ${MERCHANT_TERMS.en.oneLower} ({{merchant}}).`, // new — needs native review
      MERCHANT_HAS_OTHER_CODE: `The ${MERCHANT_TERMS.en.oneLower} already has another code: {{code}}.`, // new — needs native review
      AREA_AMBIGUOUS: 'Area {{area}} exists in several zones ({{zones}}); add the zone.', // new — needs native review
      BRANCH_DELETED: 'Branch {{branch}} is deleted.', // new — needs native review
      BRANCH_CODE_TAKEN: 'Branch code {{code}} belongs to another branch.', // new — needs native review
      CONCURRENT_CHANGE: 'Someone else changed the data meanwhile; run the import again.', // new — needs native review
      BRANCH_EXISTS: 'The branch already exists (updating existing branches is off).', // new — needs native review
      NO_CHANGES: 'Nothing to change: the row matches the branch.', // new — needs native review
    },
  },
};
